/**
 * The grade wording for the answers chosen so far, fetched again whenever one that picks the
 * template changes. A late answer for answers that have since changed is dropped, and the wording
 * on screen always belongs to the current answers (or there is none).
 */
import { useEffect, useState } from 'react';
import { fetchGradeWording } from '../../services/referenceLetterFormApi';
import type { GradeWording, WordingQuery } from '../../types/referenceLetterForm';
import { MID_TERM, type Answers } from './requestForm';

/** The query for these answers, or null until the person, the type and the language are chosen. */
export function wordingQuery(a: Answers): WordingQuery | null {
  if (!a.employeeId || !a.type || !a.language) return null;
  return {
    employeeId: a.employeeId,
    type: a.type,
    language: a.language,
    ...(a.isLeader ? { isLeader: a.isLeader } : {}),
    ...(a.type !== MID_TERM && a.reason ? { exitReason: a.reason } : {}),
  };
}

/** @returns the wording for the current answers, and whether it failed to load */
export function useGradeWording(answers: Answers): { wording: GradeWording | null; failed: boolean } {
  const query = wordingQuery(answers);
  const key = query ? JSON.stringify(query) : null;
  const [result, setResult] = useState<{ key: string; wording: GradeWording | null; failed: boolean } | null>(null);

  useEffect(() => {
    if (!key) return undefined;
    let current = true;
    fetchGradeWording(JSON.parse(key) as WordingQuery)
      .then((wording) => current && setResult({ key, wording, failed: false }))
      .catch(() => current && setResult({ key, wording: null, failed: true }));
    return () => {
      current = false;
    };
  }, [key]);

  // Only an answer to THESE answers counts; an older one is not shown while the new one loads.
  return result && result.key === key ? { wording: result.wording, failed: result.failed } : { wording: null, failed: false };
}
